import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import type { Json } from '@/integrations/supabase/types';

export interface EligibilityCriterion {
  key: string;
  label: string;
  required?: boolean;
}

export interface EligibilityField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'date';
  options?: string[];
  required?: boolean;
}

export interface EligibilitySchema {
  type: 'merit' | 'humanitarian' | 'family' | 'employment';
  category: string;
  criteria?: EligibilityCriterion[];
  min_criteria?: number;
  narrative_required?: boolean;
  narrative_label?: string;
  evidence_types?: EligibilityCriterion[];
  additional_fields?: EligibilityField[];
}

interface EligibilitySchemaEditorProps {
  value: EligibilitySchema | null;
  onChange: (schema: EligibilitySchema | null) => void;
}

const SCHEMA_TYPES = [
  { value: 'merit', label: 'Mérito (EB1A, EB1B, O-1, etc.)' },
  { value: 'humanitarian', label: 'Humanitário (SIJS, T-Visa, U-Visa, etc.)' },
  { value: 'family', label: 'Familiar (I-130, etc.)' },
  { value: 'employment', label: 'Emprego (EB2, EB3, PERM, etc.)' },
];

const DEFAULT_MERIT_CRITERIA: EligibilityCriterion[] = [
  { key: 'awards', label: 'National/International Awards' },
  { key: 'membership', label: 'Membership in Associations' },
  { key: 'published_material', label: 'Published Material About You' },
  { key: 'judging', label: 'Judging Work of Others' },
  { key: 'original_contributions', label: 'Original Contributions' },
  { key: 'scholarly_articles', label: 'Scholarly Articles' },
  { key: 'exhibitions', label: 'Artistic Exhibitions' },
  { key: 'leading_role', label: 'Leading/Critical Role' },
  { key: 'high_salary', label: 'High Salary' },
  { key: 'commercial_success', label: 'Commercial Success' },
];

export default function EligibilitySchemaEditor({ value, onChange }: EligibilitySchemaEditorProps) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(!!value);

  const handleEnableChange = (checked: boolean) => {
    setEnabled(checked);
    if (!checked) {
      onChange(null);
    } else {
      onChange({
        type: 'humanitarian',
        category: '',
        narrative_required: true,
        narrative_label: 'Descrição da situação',
        evidence_types: [],
        additional_fields: [],
      });
    }
  };

  const handleTypeChange = (type: EligibilitySchema['type']) => {
    if (type === 'merit') {
      onChange({
        type: 'merit',
        category: value?.category || '',
        criteria: DEFAULT_MERIT_CRITERIA,
        min_criteria: 3,
        additional_fields: value?.additional_fields || [],
      });
    } else {
      onChange({
        type,
        category: value?.category || '',
        narrative_required: true,
        narrative_label: 'Descrição da situação',
        evidence_types: value?.evidence_types || [],
        additional_fields: value?.additional_fields || [],
      });
    }
  };

  const addCriterion = () => {
    if (!value) return;
    const newCriterion: EligibilityCriterion = {
      key: `criterion_${Date.now()}`,
      label: '',
      required: false,
    };
    if (value.type === 'merit') {
      onChange({ ...value, criteria: [...(value.criteria || []), newCriterion] });
    } else {
      onChange({ ...value, evidence_types: [...(value.evidence_types || []), newCriterion] });
    }
  };

  const updateCriterion = (index: number, updates: Partial<EligibilityCriterion>) => {
    if (!value) return;
    if (value.type === 'merit') {
      const criteria = [...(value.criteria || [])];
      criteria[index] = { ...criteria[index], ...updates };
      onChange({ ...value, criteria });
    } else {
      const evidence_types = [...(value.evidence_types || [])];
      evidence_types[index] = { ...evidence_types[index], ...updates };
      onChange({ ...value, evidence_types });
    }
  };

  const removeCriterion = (index: number) => {
    if (!value) return;
    if (value.type === 'merit') {
      const criteria = [...(value.criteria || [])];
      criteria.splice(index, 1);
      onChange({ ...value, criteria });
    } else {
      const evidence_types = [...(value.evidence_types || [])];
      evidence_types.splice(index, 1);
      onChange({ ...value, evidence_types });
    }
  };

  const addField = () => {
    if (!value) return;
    const newField: EligibilityField = {
      key: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
    };
    onChange({ ...value, additional_fields: [...(value.additional_fields || []), newField] });
  };

  const updateField = (index: number, updates: Partial<EligibilityField>) => {
    if (!value) return;
    const additional_fields = [...(value.additional_fields || [])];
    additional_fields[index] = { ...additional_fields[index], ...updates };
    onChange({ ...value, additional_fields });
  };

  const removeField = (index: number) => {
    if (!value) return;
    const additional_fields = [...(value.additional_fields || [])];
    additional_fields.splice(index, 1);
    onChange({ ...value, additional_fields });
  };

  const criteriaList = value?.type === 'merit' ? value.criteria : value?.evidence_types;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Configuração de Elegibilidade</CardTitle>
          <Switch checked={enabled} onCheckedChange={handleEnableChange} />
        </div>
        <p className="text-xs text-muted-foreground">
          Define os campos que o vendedor deve preencher para validar a elegibilidade do cliente
        </p>
      </CardHeader>

      {enabled && value && (
        <CardContent className="space-y-4">
          {/* Type Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Processo</Label>
              <Select value={value.type} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEMA_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Categoria/Código</Label>
              <Input
                value={value.category}
                onChange={(e) => onChange({ ...value, category: e.target.value })}
                placeholder="Ex: EB1A, SIJS, T-Visa"
              />
            </div>
          </div>

          {/* Merit-specific: Min Criteria */}
          {value.type === 'merit' && (
            <div className="space-y-2">
              <Label>Mínimo de critérios a atender</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={value.min_criteria || 3}
                onChange={(e) => onChange({ ...value, min_criteria: parseInt(e.target.value) || 3 })}
              />
            </div>
          )}

          {/* Humanitarian-specific: Narrative */}
          {value.type !== 'merit' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={value.narrative_required || false}
                  onCheckedChange={(checked) => onChange({ ...value, narrative_required: checked })}
                />
                <Label>Narrativa obrigatória</Label>
              </div>
              {value.narrative_required && (
                <Input
                  value={value.narrative_label || ''}
                  onChange={(e) => onChange({ ...value, narrative_label: e.target.value })}
                  placeholder="Label do campo de narrativa"
                />
              )}
            </div>
          )}

          {/* Criteria/Evidence Types */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>{value.type === 'merit' ? 'Critérios de Elegibilidade' : 'Tipos de Evidência'}</Label>
              <Button variant="outline" size="sm" onClick={addCriterion}>
                <Plus className="h-3 w-3 mr-1" />
                Adicionar
              </Button>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(criteriaList || []).map((criterion, index) => (
                <div key={criterion.key} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <Input
                    value={criterion.label}
                    onChange={(e) => updateCriterion(index, { label: e.target.value })}
                    placeholder="Label do critério"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCriterion(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Fields */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Campos Adicionais</Label>
              <Button variant="outline" size="sm" onClick={addField}>
                <Plus className="h-3 w-3 mr-1" />
                Adicionar
              </Button>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(value.additional_fields || []).map((field, index) => (
                <div key={field.key} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                  <Input
                    value={field.label}
                    onChange={(e) => updateField(index, { label: e.target.value })}
                    placeholder="Label do campo"
                    className="flex-1"
                  />
                  <Select
                    value={field.type}
                    onValueChange={(type: EligibilityField['type']) => updateField(index, { type })}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Texto</SelectItem>
                      <SelectItem value="number">Número</SelectItem>
                      <SelectItem value="boolean">Sim/Não</SelectItem>
                      <SelectItem value="date">Data</SelectItem>
                      <SelectItem value="select">Seleção</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={field.required || false}
                      onCheckedChange={(checked) => updateField(index, { required: checked })}
                    />
                    <span className="text-xs">Obrig.</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeField(index)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Preview Badge */}
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Preview:</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{value.type}</Badge>
              {value.category && <Badge>{value.category}</Badge>}
              {value.type === 'merit' && value.min_criteria && (
                <Badge variant="secondary">Min. {value.min_criteria} critérios</Badge>
              )}
              {(criteriaList?.length || 0) > 0 && (
                <Badge variant="secondary">{criteriaList?.length} {value.type === 'merit' ? 'critérios' : 'evidências'}</Badge>
              )}
              {(value.additional_fields?.length || 0) > 0 && (
                <Badge variant="secondary">{value.additional_fields?.length} campos extras</Badge>
              )}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
